import { useState } from "react";

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Toolbar from "@mui/material/Toolbar";

const createModalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
	height: 400,
  backgroundColor: "background.paper",
  borderRadius: "6px",
  boxShadow: 24,
  p: 4,
	display: "flex",
	flexDirection: "column"
};

const doneContent = <>
	<Typography variant="h6">Time to get your leads!</Typography>
	<p>
		You are now ready to have your in-market leads seamlessly delivered to your CRM or Engagement platform every day.
		Let's see your sales skyrocket!
	</p>
</>

const steps = [
	{
		label: 'Connection',
		content: <>
			<Typography variant="h6">Integration Management Page</Typography>
			<p>Here is where you manage all the settings surrounding your connection.</p>
			<p> This includes:</p>
			<ul>
				<li>Remove your connection via the "Turn Off" button</li>
				<li>Control how companies and contacts are assigned to users with our Rules Engine</li>
				<li>Manage which properties our data fills your downstream platform</li>
			</ul>
		</>
	},
	{
		label: 'Rules Engine',
		content: <>
			<Typography variant="h6">Rules Engine</Typography>
			<p>
				A rule attaches your account's connection to each of Revenue Targeting Models.
				Without a rule associated to a Revenue Targeting Model, the data from that Model will not be populated into your downstream platform.
			</p>
			<p>
				Rules also control how the companies and contacts we load into the downstream platform are assigned to your users.
				Create a rule, choose a rule type, and specify the associated users to get started!
			</p>
			<p>
				Click the <Box component={"span"} sx={{color:"#009cf8"}}>Next</Box> button to learn about the available rule types. 
			</p>
		</>
	},
	{
		label: 'Rule Types',
		content: <>
			<Typography variant="h6">Rule Types</Typography>
			<Box fontWeight={"bold"} sx={{mt:1, color:"#009cf8"}}>Random</Box>
			<ul style={{marginTop:8}}>
				<li>Each company and contact will be randomly assigned to a user associated to the rule.</li>
				<li>Use this option and only define one associated user to have every company and contact have one owner.</li>
			</ul>
		</>
	},
	{
		label: 'Properties',
		content: <>
			<Typography variant="h6">Property Mapping</Typography>
			<p>
				This is where you configure what properties in your downstream platform each of our data points are entered into.
			</p>
			<p>
				For each Revenue Targeting Model with a rule associated with it, you must create a two properties on both company and contact objects.
				One for the RevScore and another for the name of the Model.
			</p>
			<p>
				Often times you will need to create a new custom property. 
				After doing so make sure to click the "Refresh Properties" button and the options will be updated.
			</p>
		</>
	}
]

export default function IntegrationHelp() {
	const [open, setOpen] = useState(false);
	const [activeStep, setActiveStep] = useState(0);

	const nextBtnDisabled = () => {
		return activeStep >= steps.length;
	};

	const backBtnDisabled = () => {
		return activeStep <= 0;
	};

	return (
		<>
			<IconButton
				aria-label="integration help"
				size="large"
				color="primary"
				onClick={() => {setOpen(true)}}
				sx={{mr:"12px"}}
			>
				<HelpOutlineIcon />
			</IconButton>
			<Modal 
				open={open} 
				onClose={() => {
					setOpen(false);
					setActiveStep(0);
				}}
			>
				<Box sx={createModalStyles}>
					<Stepper activeStep={activeStep} alternativeLabel>
						{steps.map((step) => {
							return (
								<Step key={step.label}>
									<StepLabel>{step.label}</StepLabel>
								</Step>
							);
						})}
					</Stepper>
					<Stack height={1}>
						<Box flex={1} sx={{mt:2}}>
							{activeStep >= steps.length 
								? doneContent
								: steps[activeStep].content
							}
						</Box>
						<Toolbar sx={{ padding: "0 !important" }}>
							<Box  flex={1}>
								<Button
									disabled={backBtnDisabled()}
									onClick={() => {setActiveStep(activeStep-1)}}
								>
									Back
								</Button>
							</Box>
							<Box>
								<Button
									disabled={nextBtnDisabled()}
									onClick={() => {setActiveStep(activeStep+1)}}
								>
									Next
								</Button>
							</Box>
						</Toolbar>
					</Stack>
				</Box>
			</Modal>
		</>
	)
}